import React, { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Users,
  UserPlus,
  Hash,
  Phone,
  Mail,
  Search,
  Copy,
  Check,
} from 'lucide-react';
import {
  Card,
  EmptyState,
  Pagination,
  SearchInput,
  LoadingSpinner,
  ErrorMessage,
  Table,
  Button,
  Modal,
  Input,
} from '@/components/ui';
import { clientsApi } from '@/services/api/clientsApi';
import type { StaffRegisterCustomerData } from '@/services/api/clientsApi';
import type { User } from '@/types/models';
import toast from 'react-hot-toast';
import type { AxiosError } from 'axios';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const ITEMS_PER_PAGE = 20;

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

type ModalState =
  | { step: 'search' }
  | { step: 'found'; user: User }
  | { step: 'register'; atomiId: number };

const registerSchema = z
  .object({
    atomi_id: z.number(),
    full_name: z.string().min(1, 'Введите ФИО'),
    phone: z.string().min(1, 'Введите телефон'),
    email: z.string().email('Некорректный email').optional().or(z.literal('')),
    password: z.string().min(8, 'Минимум 8 символов'),
    confirm_password: z.string().min(1, 'Подтвердите пароль'),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: 'Пароли не совпадают',
    path: ['confirm_password'],
  });

type RegisterForm = z.infer<typeof registerSchema>;

const AtomiSearchModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onRegistered: () => void;
}> = ({ isOpen, onClose, onRegistered }) => {
  const [modalState, setModalState] = useState<ModalState>({ step: 'search' });
  const [atomiInput, setAtomiInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [registering, setRegistering] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetForm,
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema) as any,
  });

  const handleClose = useCallback(() => {
    setModalState({ step: 'search' });
    setAtomiInput('');
    setSearching(false);
    setRegistering(false);
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSearch = useCallback(async () => {
    const id = parseInt(atomiInput, 10);
    if (isNaN(id) || id <= 0) {
      toast.error('Введите корректный Atomi ID');
      return;
    }
    setSearching(true);
    try {
      const user = await clientsApi.searchByAtomiId(id);
      setModalState({ step: 'found', user });
    } catch (err) {
      const axiosErr = err as AxiosError;
      if (axiosErr.response?.status === 404) {
        setModalState({ step: 'register', atomiId: id });
        resetForm({
          atomi_id: id,
          full_name: '',
          phone: '',
          email: '',
          password: '',
          confirm_password: '',
        });
      } else {
        toast.error(extractErrorMessage(err, 'Ошибка при поиске клиента'));
      }
    } finally {
      setSearching(false);
    }
  }, [atomiInput, resetForm]);

  const onRegisterSubmit = useCallback(
    async (data: RegisterForm) => {
      setRegistering(true);
      try {
        const payload: StaffRegisterCustomerData = {
          atomi_id: data.atomi_id,
          full_name: data.full_name,
          phone: data.phone,
          password: data.password,
          email: data.email || undefined,
        };
        await clientsApi.registerClient(payload);
        toast.success('Клиент зарегистрирован');
        onRegistered();
        handleClose();
      } catch (err) {
        const axiosErr = err as AxiosError<{ detail?: string }>;
        if (axiosErr.response?.status === 409) {
          toast.error(extractErrorMessage(err, 'Клиент уже существует'));
        } else {
          toast.error(extractErrorMessage(err, 'Ошибка регистрации'));
        }
      } finally {
        setRegistering(false);
      }
    },
    [onRegistered, handleClose],
  );

  const modalTitle =
    modalState.step === 'search'
      ? 'Поиск клиента'
      : modalState.step === 'found'
        ? 'Клиент найден'
        : 'Регистрация клиента';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={modalTitle} size="md">
      {modalState.step === 'search' && (
        <div className="space-y-4">
          <Input
            label="Atomi ID"
            type="number"
            value={atomiInput}
            onChange={(e) => setAtomiInput(e.target.value)}
            placeholder="Введите Atomi ID"
            icon={<Hash className="w-4 h-4" />}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
          <Button onClick={handleSearch} loading={searching} className="w-full">
            <Search className="w-4 h-4" />
            Найти
          </Button>
        </div>
      )}

      {modalState.step === 'found' && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            {[
              { icon: Users, label: 'ФИО', value: modalState.user.full_name },
              { icon: Phone, label: 'Телефон', value: modalState.user.phone ?? '—' },
              { icon: Mail, label: 'Email', value: modalState.user.email ?? '—' },
              { icon: Hash, label: 'Atomi ID', value: String(modalState.user.id) },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="flex items-center gap-3">
                <Icon className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="text-sm text-gray-500 w-24 shrink-0">{label}</span>
                <span className="text-sm text-gray-900 font-medium">{value}</span>
              </div>
            ))}
          </div>
          <Button variant="secondary" onClick={handleClose} className="w-full">
            Закрыть
          </Button>
        </div>
      )}

      {modalState.step === 'register' && (
        <form onSubmit={handleSubmit(onRegisterSubmit)} className="space-y-4">
          <Input
            label="Atomi ID"
            type="number"
            {...register('atomi_id', { valueAsNumber: true })}
            readOnly
            icon={<Hash className="w-4 h-4" />}
          />
          <Input
            label="ФИО"
            {...register('full_name')}
            error={errors.full_name?.message}
            required
            placeholder="Иванов Иван Иванович"
            icon={<Users className="w-4 h-4" />}
          />
          <Input
            label="Телефон"
            {...register('phone')}
            error={errors.phone?.message}
            required
            placeholder="+7XXXXXXXXXX"
            icon={<Phone className="w-4 h-4" />}
          />
          <Input
            label="Email"
            type="email"
            {...register('email')}
            error={errors.email?.message}
            placeholder="client@example.com"
            icon={<Mail className="w-4 h-4" />}
          />
          <Input
            label="Пароль"
            type="password"
            {...register('password')}
            error={errors.password?.message}
            required
            placeholder="Минимум 8 символов"
          />
          <Input
            label="Подтверждение пароля"
            type="password"
            {...register('confirm_password')}
            error={errors.confirm_password?.message}
            required
            placeholder="Повторите пароль"
          />
          <Button type="submit" loading={registering} className="w-full">
            <UserPlus className="w-4 h-4" />
            Зарегистрировать
          </Button>
        </form>
      )}
    </Modal>
  );
};

const confirmSchema = z
  .object({
    password: z.string().min(8, 'Минимум 8 символов'),
    confirmPassword: z.string().min(1, 'Подтвердите пароль'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Пароли не совпадают',
    path: ['confirmPassword'],
  });

type ConfirmForm = z.infer<typeof confirmSchema>;

const ConfirmCustomerModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  user: User;
  onConfirmed: (token: string) => void;
}> = ({ isOpen, onClose, user, onConfirmed }) => {
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetForm,
  } = useForm<ConfirmForm>({
    resolver: zodResolver(confirmSchema) as any,
  });

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const onSubmit = useCallback(
    async (data: ConfirmForm) => {
      setSubmitting(true);
      try {
        const response = await clientsApi.confirmClient(user.id, {
          password: data.password,
        });
        toast.success('Клиент подтверждён');
        onConfirmed(response.magic_link_token);
        resetForm();
      } catch (err) {
        toast.error(extractErrorMessage(err, 'Ошибка подтверждения'));
      } finally {
        setSubmitting(false);
      }
    },
    [user.id, onConfirmed, resetForm],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Подтверждение клиента" size="md">
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
          {[
            { icon: Users, label: 'ФИО', value: user.full_name },
            { icon: Phone, label: 'Телефон', value: user.phone ?? '—' },
            { icon: Mail, label: 'Email', value: user.email ?? '—' },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="flex items-center gap-3">
              <Icon className="w-4 h-4 text-gray-400 shrink-0" />
              <span className="text-sm text-gray-500 w-24 shrink-0">{label}</span>
              <span className="text-sm text-gray-900 font-medium">{value}</span>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label="Пароль"
            type="password"
            {...register('password')}
            error={errors.password?.message}
            required
            placeholder="Минимум 8 символов"
          />
          <Input
            label="Подтверждение пароля"
            type="password"
            {...register('confirmPassword')}
            error={errors.confirmPassword?.message}
            required
            placeholder="Повторите пароль"
          />
          <Button type="submit" loading={submitting} className="w-full">
            <Check className="w-4 h-4" />
            Подтвердить клиента
          </Button>
        </form>
      </div>
    </Modal>
  );
};

const MagicLinkResultModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  magicLinkToken: string;
}> = ({ isOpen, onClose, magicLinkToken }) => {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/magic-link/${magicLinkToken}`;

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Ссылка скопирована');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Не удалось скопировать');
    }
  }, [url]);

  const handleClose = useCallback(() => {
    setCopied(false);
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Ссылка для входа" size="md">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <input
            type="text"
            readOnly
            value={url}
            className="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 font-mono select-all focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {copied ? (
              <Check className="w-4 h-4 text-emerald-600" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>
        </div>

        <p className="text-sm text-gray-500 leading-relaxed">
          Отправьте эту ссылку клиенту. При переходе по ссылке клиент автоматически войдёт в систему
          и сможет установить свой пароль. Ссылка действительна 24 часа.
        </p>

        <Button variant="secondary" onClick={handleClose} className="w-full">
          Закрыть
        </Button>
      </div>
    </Modal>
  );
};

const ClientsPage: React.FC = () => {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmUser, setConfirmUser] = useState<User | null>(null);
  const [magicLinkToken, setMagicLinkToken] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['clients', search, page],
    queryFn: () =>
      clientsApi.getClients({
        search: search || undefined,
        page,
        limit: ITEMS_PER_PAGE,
      }),
  });

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleRegistered = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['clients'] });
  }, [qc]);

  const handleConfirmed = useCallback(
    (token: string) => {
      setConfirmUser(null);
      setMagicLinkToken(token);
      qc.invalidateQueries({ queryKey: ['clients'] });
    },
    [qc],
  );

  const clients = data?.items ?? [];
  const totalCount = data?.total ?? 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Клиенты</h1>
          <p className="text-sm text-gray-500 mt-1">
            Клиенты вашего филиала
            {totalCount > 0 && <span className="text-gray-400"> • {totalCount} всего</span>}
          </p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <UserPlus className="w-4 h-4" />
          Зарегистрировать клиента
        </Button>
      </div>

      <Card>
        <Card.Header>
          <SearchInput
            value={search}
            onChange={handleSearchChange}
            placeholder="Поиск по имени, телефону или email..."
            className="w-full"
          />
        </Card.Header>

        <Card.Body className="p-0">
          {isLoading ? (
            <LoadingSpinner size="lg" className="py-16" />
          ) : error ? (
            <ErrorMessage
              message="Не удалось загрузить клиентов"
              onRetry={() => refetch()}
              className="py-12"
            />
          ) : clients.length === 0 ? (
            <EmptyState
              icon={<Users className="w-8 h-8" />}
              title="Клиентов не найдено"
              description={
                search
                  ? 'Попробуйте изменить параметры поиска'
                  : 'Клиенты пока не зарегистрированы'
              }
            />
          ) : (
            <>
              {/* Mobile */}
              <div className="md:hidden divide-y divide-gray-100">
                {clients.map((client) => (
                  <div
                    key={client.id}
                    className="px-4 py-3.5 space-y-1"
                  >
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-900">{client.full_name}</p>
                      {client.is_pending ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                          Ожидает подтверждения
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Активен
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      {client.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {client.phone}
                        </span>
                      )}
                      {client.email && (
                        <span className="flex items-center gap-1 truncate">
                          <Mail className="w-3 h-3" />
                          {client.email}
                        </span>
                      )}
                    </div>
                    {client.is_pending && (
                      <button
                        onClick={() => setConfirmUser(client)}
                        className="text-sm text-indigo-600 hover:text-indigo-800 font-medium mt-1"
                      >
                        Подтвердить
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Desktop */}
              <div className="hidden md:block">
                <Table>
                  <Table.Header>
                    <Table.Row>
                      <Table.Head>ФИО</Table.Head>
                      <Table.Head>Atomi ID</Table.Head>
                      <Table.Head>Телефон</Table.Head>
                      <Table.Head>Email</Table.Head>
                      <Table.Head>Статус</Table.Head>
                      <Table.Head>Дата регистрации</Table.Head>
                      <Table.Head>{' '}</Table.Head>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {clients.map((client) => (
                      <Table.Row key={client.id}>
                        <Table.Cell className="font-medium text-gray-900">
                          {client.full_name}
                        </Table.Cell>
                        <Table.Cell>
                          {client.atomi_id != null ? (
                            <span className="font-mono text-xs text-gray-600">{client.atomi_id}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <span className="text-gray-600">{client.phone ?? '—'}</span>
                        </Table.Cell>
                        <Table.Cell>
                          <span className="text-gray-600">{client.email ?? '—'}</span>
                        </Table.Cell>
                        <Table.Cell>
                          {client.is_pending ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                              Ожидает подтверждения
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                              Активен
                            </span>
                          )}
                        </Table.Cell>
                        <Table.Cell>{formatDate(client.created_at)}</Table.Cell>
                        <Table.Cell>
                          {client.is_pending ? (
                            <button
                              onClick={() => setConfirmUser(client)}
                              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              Подтвердить
                            </button>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            </>
          )}
        </Card.Body>

        {totalPages > 1 && (
          <Card.Footer>
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </Card.Footer>
        )}
      </Card>

      <AtomiSearchModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onRegistered={handleRegistered}
      />

      {confirmUser && (
        <ConfirmCustomerModal
          isOpen={!!confirmUser}
          onClose={() => setConfirmUser(null)}
          user={confirmUser}
          onConfirmed={handleConfirmed}
        />
      )}

      {magicLinkToken && (
        <MagicLinkResultModal
          isOpen={!!magicLinkToken}
          onClose={() => setMagicLinkToken(null)}
          magicLinkToken={magicLinkToken}
        />
      )}
    </div>
  );
};

export default ClientsPage;
